import React, { useState, useEffect } from 'react';
import { number, bool } from 'prop-types';
import { TableCustom, Modal, MessageModal, FormError, InputFile, FormLabel, Loading } from '../../common';
import { APIS } from '../../../config/apis';
import { ServerRequest } from '../../../utils/apiweb';
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage, useForm } from '@laramie/ui';
import { OpenObjectURL } from '../../../utils/helpers';
import { getDateToString } from '../../../utils/convert';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';
import { useFormErrors } from '../../hooks/useFormErrors';


// Los archivos de una presentación no se borran nunca. Lo único que admite baja —y es baja
// lógica, el archivo queda— es lo que agregó el municipio después de la presentación.
const ORIGEN = {
    FORMULARIO: 'FORMULARIO',
    CONTRIBUYENTE: 'CONTRIBUYENTE',
    MUNICIPIO: 'MUNICIPIO'
};

// Mismo tope que valida el webapi, chequeado en el momento para no enterarse del rechazo
// recien al confirmar. Ojo: InputFile lee el archivo entero con readAsArrayBuffer y recien
// despues invoca el callback, asi que esto corre con el binario ya en memoria — evita la
// subida, no la lectura.
const MAX_BYTES_POR_ARCHIVO = 10 * 1024 * 1024;

// Mismo largo que la columna y que valida el webapi.
const MAX_LARGO_NOMBRE = 250;

const ORIGEN_DESCRIPCION = {
    [ORIGEN.FORMULARIO]: 'Formulario del sistema',
    [ORIGEN.CONTRIBUYENTE]: 'Adjuntó el contribuyente',
    [ORIGEN.MUNICIPIO]: 'Agregó el municipio'
};

const DeclaracionJuradaArchivosGrid = (props) => {

    const [state, setState] = useState({
        list: [],
        showForm: false,
        showMessage: false,
        rowForm: null
    });
    const [loading, setLoading] = useState(false);
    const [archivoPendiente, setArchivoPendiente] = useState(null);
    const [formValues, formHandle, , formSet] = useForm({
        Archivo_nombre: '',
        Archivo_path: '',
        Archivo_descripcion: ''
    });

    const mount = () => {
        if (props.idDeclaracionJurada > 0) {
            Load();
        }
    };
    useEffect(mount, [props.idDeclaracionJurada]);

    //funciones
    function Load() {
        setLoading(true);
        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.DECLARACION_JURADA,
            `/${props.idDeclaracionJurada}`,
            null,
            (response) => {
                response.json()
                    .then((data) => {
                        setLoading(false);
                        setState(prevState => {
                            return {...prevState, list: data.archivos ?? []};
                        });
                    })
                    .catch(callbackError);
            },
            callbackNoSuccess,
            callbackError
        );
    }

    const callbackNoSuccess = (response) => {
        setLoading(false);
        response.json()
            .then((error) => ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message))
            .catch(callbackError);
    };

    const callbackError = (error) => {
        setLoading(false);
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, 'Error procesando solicitud: ' + error);
    };

    //handles
    const handleClickDownload = (id) => {
        const row = state.list.find(x => x.id === id);
        setLoading(true);
        ServerRequest(
            REQUEST_METHOD.GET,
            {"Content-Type": "application/octet-stream"},
            true,
            APIS.URLS.FILE,
            `/${row.path}`,
            null,
            (response) => {
                response.blob()
                    .then((buffer) => {
                        setLoading(false);
                        OpenObjectURL(row.nombre, buffer);
                    })
                    .catch(callbackError);
            },
            callbackNoSuccess,
            callbackError
        );
    };

    const { errorOf, validate, reset } = useFormErrors(getErrors, getFormatErrors);

    const handleClickAdd = () => {
        reset();
        setArchivoPendiente(null);
        formSet({Archivo_nombre: '', Archivo_path: '', Archivo_descripcion: ''});
        setState(prevState => {
            return {...prevState, showForm: true};
        });
    };

    function getErrors() {
        const errors = {};

        if (!archivoPendiente || formValues.Archivo_nombre.length <= 0) errors.Archivo_nombre = VALIDATION_MESSAGE.REQUIRED;
        if (formValues.Archivo_descripcion.length <= 0) errors.Archivo_descripcion = VALIDATION_MESSAGE.REQUIRED;

        return errors;
    }

    function getFormatErrors() {
        const errors = {};

        // el nombre se valida acá y no solo en el backend: si el rechazo llega después de subir,
        // el binario ya quedó en el directorio temporal y nada lo referencia.
        if (formValues.Archivo_nombre.length > MAX_LARGO_NOMBRE) errors.Archivo_nombre = `No puede exceder los ${MAX_LARGO_NOMBRE} caracteres`;
        if (formValues.Archivo_descripcion.length > 250) errors.Archivo_descripcion = 'No puede exceder los 250 caracteres';

        return errors;
    }

    // El binario se retiene en memoria y recien se sube al confirmar: si el operador cancela
    // o cambia de archivo, no queda nada colgado en el directorio temporal.
    const handleUploadArchivo = (filename, data) => {
        if (data.byteLength > MAX_BYTES_POR_ARCHIVO) {
            ShowToastMessage(ALERT_TYPE.ALERT_WARNING, 'El archivo supera el tamaño máximo de 10 MB');
            return;
        }
        setArchivoPendiente(data);
        formSet({...formValues, Archivo_nombre: filename});
    };

    const subirArchivoPendiente = () => {
        return new Promise((resolve, reject) => {
            ServerRequest(
                REQUEST_METHOD.POST,
                {"Content-Type": "application/octet-stream"},
                true,
                APIS.URLS.FILE,
                null,
                archivoPendiente,
                (response) => { response.text().then(resolve).catch(reject); },
                (response) => { callbackNoSuccess(response); reject(response); },
                // el callbackError del componente muestra el toast: sin el, un fallo de red deja
                // al operador en el formulario sin saber por que no se agrego el archivo.
                (error) => { callbackError(error); reject(error); },
                false
            );
        });
    };

    const handleClickConfirmarAlta = () => {
        if (!validate()) return;

        setLoading(true);
        // El origen y la declaración jurada los fija el backend: acá solo va el archivo.
        subirArchivoPendiente()
            .then((path) => {
                ServerRequest(
                    REQUEST_METHOD.POST,
                    null,
                    true,
                    APIS.URLS.DECLARACION_JURADA,
                    `/${props.idDeclaracionJurada}/archivo`,
                    {
                        nombre: formValues.Archivo_nombre,
                        path: path,
                        descripcion: formValues.Archivo_descripcion
                    },
                    () => {
                        setLoading(false);
                        setArchivoPendiente(null);
                        setState(prevState => {
                            return {...prevState, showForm: false};
                        });
                        ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Archivo agregado');
                        Load();
                    },
                    callbackNoSuccess,
                    callbackError
                );
            })
            .catch(() => setLoading(false));
    };

    const handleClickBaja = (id) => {
        setState(prevState => {
            return {...prevState, showMessage: true, rowForm: state.list.find(x => x.id === id)};
        });
    };

    const handleConfirmarBaja = () => {
        const id = state.rowForm.id;
        setState(prevState => {
            return {...prevState, showMessage: false, rowForm: null};
        });
        setLoading(true);
        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.DECLARACION_JURADA,
            `/archivo/${id}`,
            null,
            () => {
                setLoading(false);
                ShowToastMessage(ALERT_TYPE.ALERT_SUCCESS, 'Archivo dado de baja');
                Load();
            },
            callbackNoSuccess,
            callbackError
        );
    };

    //celdas
    const cellHeaderAdd = <div className='action'>
                              {!props.disabled && (
                                  <div onClick={() => handleClickAdd()} className="link">
                                      <span className="material-symbols-outlined" title="Agregar archivo">add</span>
                                  </div>
                              )}
                          </div>;

    const cellAcciones = (data) => {
        const row = state.list.find(x => x.id === data.value);
        // Solo lo que agregó el municipio se puede dar de baja. El formulario del sistema y lo
        // que acompañó el contribuyente son la evidencia de la presentación.
        const permiteBaja = !props.disabled && row && row.origen === ORIGEN.MUNICIPIO;

        return <div className='action'>
                   <div onClick={() => handleClickDownload(data.value)} className="link">
                       <span className="material-symbols-outlined" title="Descargar">download</span>
                   </div>
                   {permiteBaja && (
                       <div onClick={() => handleClickBaja(data.value)} className="link">
                           <span className="material-symbols-outlined" title="Dar de baja">delete</span>
                       </div>
                   )}
               </div>;
    };

    const tableColumns = [
        { Header: 'Origen', Cell: (data) => ORIGEN_DESCRIPCION[data.value] ?? data.value, accessor: 'origen', width: '20%' },
        { Header: 'Archivo', accessor: 'nombre', width: '25%' },
        { Header: 'Descripción', accessor: 'descripcion', width: '35%' },
        { Header: 'Fecha', Cell: (data) => getDateToString(data.value, true), accessor: 'fecha', width: '15%' },
        { Header: cellHeaderAdd, Cell: cellAcciones, id: 'abm', accessor: 'id', width: '5%', disableGlobalFilter: true, disableSortBy: true }
    ];

    return (
    <>
        <Loading visible={loading}></Loading>

        {state.showMessage &&
            <MessageModal
                title={"Confirmación"}
                message={"¿Está seguro de dar de baja el archivo? El archivo se conserva, deja de listarse."}
                onDismiss={() => setState(prevState => ({...prevState, showMessage: false, rowForm: null}))}
                onConfirm={() => handleConfirmarBaja()}
            />
        }

        {state.showForm &&
            <Modal
                size="md"
                title="Agregar archivo"
                onEscape={() => setState(prevState => ({...prevState, showForm: false}))}
                body={(
                    <div className="row form-basic">
                        <div className="col-12">
                            <FormLabel htmlFor="Archivo_descripcion" required>Descripción</FormLabel>
                            <input
                                name="Archivo_descripcion"
                                type="text"
                                className={`form-control ${errorOf('Archivo_descripcion') ? 'is-invalid' : ''}`}
                                value={formValues.Archivo_descripcion}
                                onChange={formHandle}
                            />
                            <FormError message={errorOf('Archivo_descripcion')} />
                        </div>
                        <div className="col-12">
                            <FormLabel htmlFor="Archivo_nombre" required>Archivo</FormLabel>
                            <InputFile
                                name="Archivo_nombre"
                                className={`form-control ${errorOf('Archivo_nombre') ? 'is-invalid' : ''}`}
                                value={formValues.Archivo_nombre}
                                onUpload={handleUploadArchivo}
                            />
                            <FormError message={errorOf('Archivo_nombre')} />
                        </div>
                    </div>
                )}
                footer={(<>
                    <button className="btn btn-outline-primary" data-dismiss="modal"
                            onClick={() => setState(prevState => ({...prevState, showForm: false}))}>Cancelar
                    </button>
                    <button className="btn btn-primary" data-dismiss="modal"
                            onClick={() => handleClickConfirmarAlta()}>Aceptar
                    </button>
                </>)}
            />
        }

        <TableCustom
            showFilterGlobal={false}
            className={'TableCustomBase'}
            columns={tableColumns}
            data={state.list}
        />
    </>
    );
};

DeclaracionJuradaArchivosGrid.propTypes = {
    idDeclaracionJurada: number.isRequired,
    disabled: bool
};

DeclaracionJuradaArchivosGrid.defaultProps = {
    disabled: false
};

export default DeclaracionJuradaArchivosGrid;
